import { Box, Spinner } from "@primer/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Modal from "../../components/ui/modal";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { useWindowSize } from "../../hooks/useWindowSize";
import { crmHocSinhApi } from "./../../api/crmHocSinhApi";
import { crmSurveyResponseApi } from "../../api/crmSurveyResponseApi";
import BaoPhiLayout from "./bao-phi";
import CanBoNhanVienLayout from "./canbo-nhanvien/CanBoNhanVienLayout";
import { DangKyDichVu } from "./dich-vu";
import GhiDanhPage from "./ghi-danh";
import QuanHeGiaDinhLayout from "./gia-dinh/QuanHeGiaDinhLayout";
import { GiayToNopForm } from "./giay-to-nop";
import HinhThucMienGiam from "./hinh-thuc-mien-giam";
import { HocSinhProfileListTab } from "./list-tab";
import { HocSinhNameAndAvavtar } from "./name-and-avatar";
import ProfileForm from "./profile-form/ProfileForm";
import SucKhoeHocSinhPage from "./suc-khoe";
import { ThiDauVao } from "../thi-dau-vao";
import BHYTForm from "./bhyt/BHYTForm";
import QuaTrinhHocPage from "./qua-trinh-hoc/QuaTrinhHocPage";
import BusPage from "./bus/BusPage";
import BanTruPage from "./ban-tru/BanTruPage";

interface HocSinhProfileProps {
  id: number;
  crm_survey_respone_id?: number;
  value?: number;
  height?: number;
  phuHuynh?: any;
  initialTab?: string;
  hideSaveButton?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormRef {
  handleSaveChanges: () => void;
}

const ZERO_TO_UNDEFINED_KEYS = [
  "dm_trangthaihocsinh_id",
  "dm_gioitinh_id",
  "dm_quoctich_id",
  "dm_dantoc_id",
  "dm_tongiao_id",
  "dm_truong_id",
  "dm_khoi_id",
  "dm_he_id",
  "dm_lop_id",
  "dm_nguonbiettoi_id",
  "dm_quanhe_id",
  "dm_tinh_id",
  "dm_huyen_id",
  "dm_xa_id",
  "dm_tinh_ht_id",
  "dm_huyen_ht_id",
  "dm_xa_ht_id",
  "dieu_phoi_vien_id",
];

const normalizeDraftHocSinh = (raw: any, crmSurveyResponeId: number) => {
  const draft: any = {
    ...(raw || {}),
    id: 0,
    crm_survey_respone_id: raw?.crm_survey_respone_id || crmSurveyResponeId,
  };
  ZERO_TO_UNDEFINED_KEYS.forEach((key) => {
    if (!draft[key] || Number(draft[key]) <= 0) {
      draft[key] = undefined;
    }
  });
  return draft;
};

const HocSinhProfile = (props: HocSinhProfileProps) => {
  const { id } = useParams<{ id: string }>();
  const [hocSinh, setHocSinh] = useState<any>({ id: props.id });
  const [isLoading, setIsLoading] = useState(Boolean(props.crm_survey_respone_id && !(props.id > 0)));
  const [selectedTab, setSelectedTab] = useState<string>(props.initialTab || "profile");
  const [profileKey, setProfileKey] = useState(0);
  const componentDidMount = useRef(false);
  const { nam_hoc } = useCommonSelectedHook();
  const { height } = useWindowSize();
  const profileFormRef = useRef<FormRef | null>(null);

  const stableHocSinhId = useMemo(() => {
    if (hocSinh?.id > 0) return hocSinh.id;
    const targetId = props.value !== undefined && props.value >= 0
      ? props.value
      : parseInt(id || "0", 10) || props.id;
    return targetId > 0 ? targetId : (props.id > 0 ? props.id : 0);
  }, [hocSinh?.id, props.value, id, props.id]);

  const stableHocSinh = useMemo(() => {
    return {
      id: stableHocSinhId,
      ho_ten: hocSinh.ho_ten || "",
      ma_hs: hocSinh.ma_hs || "",
      ma_tn: hocSinh.ma_tn || "",
      avatar: hocSinh.avatar || "",
      dm_coso_id: hocSinh.dm_coso_id || 0,
      ten_lop: hocSinh.ten_lop || "",
    };
  }, [stableHocSinhId, hocSinh.ho_ten, hocSinh.ma_hs, hocSinh.ma_tn, hocSinh.avatar, hocSinh.dm_coso_id, hocSinh.ten_lop]);

  useEffect(() => {
    if (props.value !== undefined && props.value >= 0) {
      setHocSinh({ id: props.value });
    } else if (!props.crm_survey_respone_id || props.id > 0) {
      setHocSinh({ id: parseInt(id || "0", 10) || props.id });
    }
  }, [props.value, id, props.id, props.crm_survey_respone_id]);

  useEffect(() => {
    componentDidMount.current = true;

    const initialLoad = async () => {
      if (hocSinh.id > 0) {
        await handleGetHocSinhData(hocSinh.id);
        setProfileKey((prev) => prev + 1);
      } else if (props.crm_survey_respone_id && props.crm_survey_respone_id > 0) {
        setIsLoading(true);
        try {
          const res = await crmSurveyResponseApi.getHocSinhByResponseId(props.crm_survey_respone_id);
          if (!componentDidMount.current) return;
          if (res.is_success && res.data) {
            if (res.data.id > 0) {
              await handleGetHocSinhData(res.data.id);
            } else {
              setHocSinh(normalizeDraftHocSinh(res.data, props.crm_survey_respone_id));
            }
          } else {
            setHocSinh(normalizeDraftHocSinh({}, props.crm_survey_respone_id));
          }
          setProfileKey((prev) => prev + 1);
        } catch (error) {
          console.error("Error fetching draft student:", error);
          if (componentDidMount.current) {
            setHocSinh(normalizeDraftHocSinh({}, props.crm_survey_respone_id));
          }
        } finally {
          if (componentDidMount.current) {
            setIsLoading(false);
          }
        }
      }
    };

    initialLoad();

    return () => {
      componentDidMount.current = false;
    };
  }, [hocSinh.id, props.crm_survey_respone_id]);

  const handleGetHocSinhData = async (targetId?: number) => {
    const fetchId = targetId ?? hocSinh.id;
    if (!(fetchId > 0)) return;
    setIsLoading(true);
    try {
      const res = await crmHocSinhApi.getById(fetchId, nam_hoc);
      if (!componentDidMount.current) return;
      if (res.is_success && res.data) {
        setHocSinh(res.data);
      }
    } catch (error) {
      console.error("Error fetching student data:", error);
    } finally {
      if (componentDidMount.current) {
        setIsLoading(false);
      }
    }
  };

  const handleTabChange = async (tab: string) => {
    setSelectedTab(tab);
    if (tab === "profile" && hocSinh.id > 0) {
      await handleGetHocSinhData(hocSinh.id);
      setProfileKey((prev) => prev + 1);
    }
  };

  const isPreparingSurveyDraft = Boolean(isLoading && !(hocSinh?.id > 0) && props.crm_survey_respone_id);

  return (
    <Modal
      isOpen
      onClose={props.onClose}
      shouldCloseOnOverlayClick={false}
      sx={{
        width: "1400px",
      }}
      title={props.crm_survey_respone_id && !(stableHocSinhId > 0) ? "Tiếp nhận học sinh" : "Thông tin học sinh"}
    >
      <Box sx={{
        p: 1,
        overflow: "hidden",
      }}>
        <Box
          className="top-side"
          mb={0}
          pb={1}
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            flexGrow={1}
          >
            <Box position="relative">
              {stableHocSinh.ho_ten && (
                <Box opacity={isLoading ? 0.3 : 1}>
                  <HocSinhNameAndAvavtar hocSinh={stableHocSinh} />
                </Box>
              )}
            </Box>
            <Box flexGrow={1}>
              <HocSinhProfileListTab
                selectedTab={selectedTab}
                hocSinhId={stableHocSinhId}
                onTabSelectionChanged={handleTabChange}
              />
            </Box>
          </Box>
        </Box>

        <Box
          className="bottom-side"
          overflow="auto"
          height={props.height ?? height - 300}
        >
          <Box className="content">
            <Box p={2}>
              {isPreparingSurveyDraft ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
                  <Spinner size="medium" />
                </Box>
              ) : (
                <>
                  {selectedTab === "profile" && (
                    <ProfileForm
                      ref={profileFormRef}
                      key={`${hocSinh.id}-${profileKey}`}
                      hocSinh={hocSinh}
                      hideSaveButton={props.hideSaveButton}
                      onSubmitSuccess={(newId: any) => {
                        if (hocSinh.id > 0) {
                          handleGetHocSinhData(hocSinh.id);
                        } else if (newId > 0) {
                          setHocSinh({ id: newId });
                        }
                        props.onSuccess();
                      }}
                    />
                  )}
                  {selectedTab === "familly" && (
                    <QuanHeGiaDinhLayout hocSinh={hocSinh} />
                  )}
                  {selectedTab === "cbnv" && (
                    <CanBoNhanVienLayout hocSinh={hocSinh} />
                  )}
                  {selectedTab === "thidauvao" && (
                    <ThiDauVao hocSinh={hocSinh} />
                  )}
                  {selectedTab === "health" && (
                    <SucKhoeHocSinhPage ts_hocsinh_id={hocSinh.id} />
                  )}
                  {selectedTab === "discount" && (
                    <HinhThucMienGiam hocSinh={hocSinh} />
                  )}
                  {selectedTab === "service" && (
                    <DangKyDichVu hocSinh={hocSinh} />
                  )}
                  {selectedTab === "registration" && (
                    <GhiDanhPage hocSinh={hocSinh} />
                  )}
                  {selectedTab === "debit_note" && (
                    <BaoPhiLayout hocSinh={hocSinh} />
                  )}
                  {selectedTab === "saleset" && (
                    <DangKyDichVu hocSinh={hocSinh} />
                  )}
                  {selectedTab === "document" && (
                    <GiayToNopForm
                      hocSinh={hocSinh}
                      onUploadedAvatar={() => handleGetHocSinhData(hocSinh.id)}
                    />
                  )}
                  {selectedTab === "bhyt" && (
                    <BHYTForm ts_hocsinh_id={hocSinh.id} />
                  )}
                  {selectedTab === "history" && (
                    <QuaTrinhHocPage ts_hocsinh_id={hocSinh.id} />
                  )}
                  {selectedTab === "bus" && (
                    <BusPage ts_hocsinh_id={hocSinh.id} />
                  )}
                  {selectedTab === "boarding" && (
                    <BanTruPage ts_hocsinh_id={hocSinh.id} />
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default HocSinhProfile;
